import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'wouter';
import {
  FileText,
  Brain,
  MessageSquare,
  HelpCircle,
  Edit,
  Plus,
  BarChart3,
  Users,
  ArrowRight
} from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';

export default function ContentHubScreen() {
  // Fetch content statistics
  const { data: contentStats } = useQuery({
    queryKey: ['/api/admin/content-stats'],
    queryFn: () => apiRequest('/api/admin/content-stats').catch(() => ({
      maturity_guidance: 60,
      maturity_nudges: 88,
      maturity_next_steps: 12,
      quadrant_guidance: 4,
      value_narratives: 25,
      process_impacts: 8,
      help_content: 108,
      user_guides: 15,
      automation_benchmarks: 17,
      pillar_benchmarks: 4
    })),
    staleTime: 5 * 60 * 1000 // 5 minutes
  });

  const contentAreas = [
    {
      title: "Maturity Assessment Content",
      description: "Manage guidance, nudges, and recommendations for maturity assessments",
      icon: Brain,
      href: "/admin/maturity-content",
      stats: [
        { label: "Guidance Items", value: contentStats?.maturity_guidance || 0 },
        { label: "Strategic Nudges", value: contentStats?.maturity_nudges || 0 },
        { label: "Next Steps", value: contentStats?.maturity_next_steps || 0 },
        { label: "Quadrant Guidance", value: contentStats?.quadrant_guidance || 0 }
      ],
      color: "bg-blue-500"
    },
    {
      title: "Value Narratives",
      description: "Edit process value stories and impact descriptions shown to clients",
      icon: MessageSquare,
      href: "/admin/value-narratives",
      stats: [
        { label: "Value Stories", value: contentStats?.value_narratives || 0 },
        { label: "Process Impacts", value: contentStats?.process_impacts || 0 }
      ],
      color: "bg-green-500"
    },
    {
      title: "Help Documentation",
      description: "User-facing help content, tooltips, and guidance materials",
      icon: HelpCircle,
      href: "/admin/help-content",
      stats: [
        { label: "Help Articles", value: contentStats?.help_content || 0 },
        { label: "User Guides", value: contentStats?.user_guides || 0 }
      ],
      color: "bg-purple"
    },
    {
      title: "Benchmark Content",
      description: "Automation benchmarks and performance data for assessments",
      icon: BarChart3,
      href: "/admin/benchmarks-hub",
      stats: [
        { label: "Automation Tasks", value: contentStats?.automation_benchmarks || 0 },
        { label: "Pillar Benchmarks", value: contentStats?.pillar_benchmarks || 0 }
      ],
      color: "bg-orange-500"
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Content Management Hub</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Central dashboard for managing all platform content and guidance materials
        </p>
      </div>

      {/* Content Management Overview */}
      <Card className="border-blue-200 bg-blue-50 dark:bg-blue-900/20">
        <CardHeader>
          <CardTitle className="flex items-center text-blue-800 dark:text-blue-200">
            <FileText className="w-5 h-5 mr-2" />
            Content Management Guidelines
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div>
              <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">Content Types:</h4>
              <ul className="space-y-1 text-blue-700 dark:text-blue-300">
                <li>• <strong>Maturity Content:</strong> Assessment guidance and strategic recommendations</li>
                <li>• <strong>Value Narratives:</strong> Process improvement stories and ROI descriptions</li>
                <li>• <strong>Help Content:</strong> User documentation and step-by-step guides</li>
                <li>• <strong>Benchmarks:</strong> Industry data and automation performance metrics</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">Best Practices:</h4>
              <ul className="space-y-1 text-blue-700 dark:text-blue-300">
                <li>• Keep content clear and client-focused</li>
                <li>• Use consistent terminology across all materials</li>
                <li>• Update benchmarks regularly with current industry data</li>
                <li>• Test content changes in a staging environment first</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Content Areas Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {contentAreas.map((area) => {
          const Icon = area.icon;
          return (
            <Card key={area.href} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg ${area.color} text-white`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{area.title}</CardTitle>
                      <CardDescription className="mt-1">
                        {area.description}
                      </CardDescription>
                    </div>
                  </div>
                  <Link href={area.href}>
                    <Button variant="outline" size="sm">
                      <Edit className="w-4 h-4 mr-2" />
                      Manage
                    </Button>
                  </Link>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  {area.stats.map((stat) => (
                    <div key={stat.label} className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <div className="text-2xl font-bold text-gray-900 dark:text-white">
                        {stat.value}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">
                        {stat.label}
                      </div>
                    </div>
                  ))}
                </div>
                
                <Link href={area.href}>
                  <Button className="w-full mt-4" variant="outline">
                    <span>Manage {area.title}</span>
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>
            Common content management tasks
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            <Link href="/admin/maturity-content">
              <Button variant="outline" className="w-full justify-start">
                <Plus className="w-4 h-4 mr-2" />
                Add Maturity Nudge
              </Button>
            </Link>
            
            <Link href="/admin/value-narratives">
              <Button variant="outline" className="w-full justify-start">
                <Plus className="w-4 h-4 mr-2" />
                Create Value Story
              </Button>
            </Link>
            
            <Link href="/admin/benchmarks-hub">
              <Button variant="outline" className="w-full justify-start">
                <Plus className="w-4 h-4 mr-2" />
                Add Benchmark
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}